import { useAtom } from "jotai";

import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoGridDocuments from "components/PhotoGridDocuments";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";

import { injuredPeoplePhotosAtom } from "atoms/injuredPeoplePhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { Checkpoint } from "types/Checkpoint";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

export const DJPhotosInjuredPeoplePage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const [injuredPeoplePhotos, setInjuredPeoplePhotos] = useAtom(injuredPeoplePhotosAtom);

  const canContinue = true;

  const onContinue = () => {
    trackEvent({
      name: "Digital Journey Injured People Photos - continue",
      properties: { numberOfImages: injuredPeoplePhotos.length },
    });
    onNext();
  };

  const onDrop = (files: File[]) => {
    files.forEach(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `Injured Person photo to upload number ${index + 1}`,
        type: Photo.Additional,
      });

      setInjuredPeoplePhotos((photos) => [...photos, image]);
    });
  };

  return (
    <PageLayout
      contentSpacing="space-between"
      dataTestId="injuredPeoplePhotos"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop} disableGalleryUploadOverride={false}>
            <Button typeVariant={canContinue ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {canContinue && <Button onClick={onContinue}>{continueCTA}</Button>}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={16}>
        <Flex gap={8}>
          <Text as="h1" sizeVariant="xl" weight={600}>
            Scatta le foto del referto del Pronto Soccorso
          </Text>
          <Text>Se non hai il referto a disposizione, ti verrà chiesto di fornirlo in un secondo momento.</Text>
        </Flex>
        {Boolean(injuredPeoplePhotos.length) ? (
          <PhotoGrid images={injuredPeoplePhotos} deleteFromAtom={setInjuredPeoplePhotos} />
        ) : (
          <Flex
            align="center"
            sx={{
              "& > div": {
                width: "unset",
              },
            }}
          >
            <PhotoGridDocuments
              sxImageContainer={{
                "&&": {
                  maxHeight: "354px",
                },
              }}
              images={[
                {
                  id: "djInjuredPersonPhoto",
                  alt: "Injured Person Photos",
                  src: "/assets/png/digitalJourney/injured-people-example.png",
                },
              ]}
            />
          </Flex>
        )}
      </Flex>
    </PageLayout>
  );
};
